import {onPatch, types, getRoot, getParent} from "mobx-state-tree";
import programElements from "../assets/program-elements";
import moodBoardData from "../assets/moodboard-data";
import interactiveElementsData from "../assets/interactive-elements";
import {questionsVersion} from "../assets/interactive-elements";
import importantElements from "../assets/important-elements";

const ProgramRank = types
    .model('ProgramRank', {
        id: types.identifier,
        score: types.number,
    }).actions(self => ({
        setScore(value) {
            self.score = value;
        }
    }));

const Selection = types
    .model('Selection', {
        key: types.string,
        img: types.string,
        active: types.optional(types.boolean, false)
    }).actions(self => ({
        toggleActive() {
            if (!self.active && getParent(self, 2).activeSelections.length < 4) {
                self.active = !self.active;
            } else {
                self.active = false;
            }
        }
    }));

const Answer = types
    .model('Answer', {
        text: types.string,
        selected: types.optional(types.boolean, false)
    }).actions(self => ({
        toggleSelected() {
            const parent = getParent(self, 2);
            if (parent.limit > 0) {
                if (parent.selectedOptions.length < parent.limit) {
                    self.selected = !self.selected;
                } else {
                    self.selected = false;
                }
            } else {
                self.selected = !self.selected;
            }
        },
        setSelected(bool){
          self.selected = bool;
        }
    }));

const Question = types
    .model('Question', {
        id: types.identifier,
        type: types.string,
        question: types.string,
        options: types.array(Answer),
        limit: types.optional(types.integer, 0),
        audience: types.optional(types.array(types.string), []),
        other: types.optional(types.boolean, false),
        otherText: types.optional(types.string, "")
    }).actions(self => ({
        setOtherText(text) {
            self.otherText = text;
        },
        getAnswer(text) {
            return self.options.filter(option => option.text === text);
        },
        resetOptions(){
            self.options.forEach(option => option.setSelected(false));
        }
    })).views(self => ({
        get selectedOptions() {
            return self.options.filter(option => option.selected)
        }
    }));


const InteractiveElement = types
    .model('InteractiveElement', {
        id: types.identifier,
        questions: types.array(Question)
    });

export const PersistStore = types
    .model('PersistStore', {
        profile: types.optional(types.string, 'NO_SELECTION'),
        userId: types.optional(types.string, '0'),
        importantRanks: types.map(ProgramRank),
        programRanks: types.map(ProgramRank),
        programWildCard: types.string,
        questionsVersion: types.string,
        urlVisited: types.string,
        metadata: types.map(types.string),
        moodBoardSelections: types.array(Selection),
        interactiveElements: types.map(InteractiveElement)
    })
    .actions(self => ({
        addImportantElement(programElement) {
            self.importantRanks.put(programElement);
        },
        addProgramElement(programElement) {
            self.programRanks.put(programElement);
        },
        setProfile(profile) {
            self.profile = profile;
        },
        setMetadata(metadata) {
            self.metadata = metadata;
        },
        setUserId(userId) {
            self.userId = userId;
        },
        setScore(group, id, value) {
            const groupHash = self.getGroup(group);
            groupHash.get(id).setScore(value);
        },
        setProgramWildcard(value) {
            self.programWildcard = value;
        },
        addMoodBoardSelection(selection) {
            const newSelection = Selection.create(selection);
            self.moodBoardSelections.push(newSelection);
        },
        addInteractiveElement(element) {
            self.interactiveElements.put(element);
        }
    })).views(self => ({
        get profileSet() {
            return self.profile !== 'NOT_SET';
        },
        get noSelection() {
            return self.profile === 'NO_SELECTION';
        },
        getUserId() {
            return self.userId;
        },
        getGroup(group) {
            if (group === 'program') {
                return self.programRanks;
            }
            return self.importantRanks;
        },
        getTotalCoins(group) {
            if (group === 'program') {
                return 30;
            }
            return 10;
        },
        getScore(group, id) {
            try {
                return self.getGroup(group).get(id).score;
            } catch (e) {
                console.warn('COULD NOT GET SCORE: ' + group + ': ' + id);

                return 0;
            }
        },
        getRemainingCoins(group) {
            let totalCoins = self.getTotalCoins(group);
            const groupHash = self.getGroup(group);
            for (let p of groupHash.values()) {
                totalCoins -= p.score;
            }
            return totalCoins;
        },
        get activeSelections() {
            return self.moodBoardSelections.filter(selection => selection.active);
        },
        get selectedMoodBoardKeys() {
            return self.moodBoardSelections.filter(selection => selection.active).map(selection => selection.key);
        }
    }));
export const persistStore = PersistStore.create({
    questionsVersion: questionsVersion,
    urlVisited: window.location.href,
    metadata: {},
    programRanks: {},
    programWildCard: '',
    moodBoardSelections: [],
    interactiveElements: {}
});
programElements.forEach((group, i) => {
    group.elements.forEach((programElement, i) => {
        persistStore.addProgramElement({id: programElement.id, score: 0});
    });
});
importantElements.forEach((elem, i) => {
    persistStore.addImportantElement({id: elem.id, score: 0});
});

moodBoardData.forEach((d) => {
    persistStore.addMoodBoardSelection(d);
});

interactiveElementsData.forEach((d) => {
    d.questions.forEach((question, i) => {
        if (!question.id) {
            if (d.questions.length > 1) throw 'All questions need an id: ' + d.id;
            question.id = d.id;
        }
    });
    persistStore.addInteractiveElement(d);
});
